// src/components/common/PageHeader/PageHeader.tsx
import { Download as DownloadIcon } from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import {
    Box,
    Button,
    CircularProgress,
    InputAdornment,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useAppSelector } from "../../../app/hooks";
import { RootState } from "../../../app/store";
import { DashboardDataLoader } from "../../../pages/DashboardApp/dashboard.dataloader";
import { DealerLocationFilter } from "../../../pages/DashboardApp/dashboard.dealerlocationfilter";
import { DateRangePickerCustom } from "../daterangepicker.custom";
import { PageHeaderProps } from "./types";
/// <summary>
/// Renders a page header with configurable options like search bar,
/// dealer location filter, and date range picker.
/// Also updates the browser title using Helmet.
/// </summary>
/// <param name="title">The main title text displayed on the page header.</param>
/// <param name="browserTitle">The browser tab title displayed in the document head.</param>
/// <param name="showSearch">Whether to show the search input field.</param>
/// <param name="showDealerFilter">Whether to show the dealer location filter dropdown.</param>
/// <param name="showDateRange">Whether to show the date range picker.</param>
/// <param name="searchValue">The current search input value.</param>
/// <param name="onSearchChange">Event handler triggered when search text changes.</param>
/// <param name="onSearchEnter">Event handler triggered when Enter is pressed in the search field.</param>
/// <param name="onDealerLocationChange">Callback fired when dealer location changes.</param>
/// <param name="dateRange">The currently selected date range.</param>
/// <param name="onDateRangePickerChange">Callback fired when the date range changes.</param>
/// <param name="excelButtonProps">provides the props and handler for excel export.</param>
export default function PageHeader({
    title,
    showSearch = false,
    showDealerFilter = false,
    showDateRange = false,
    searchValue,
    onSearchChange,
    onSearchEnter,
    onDealerLocationChange,
    dateRange,
    onDateRangePickerChange,
    excelButtonProps,
    browserTitle = 'PerseusPay',    
}: PageHeaderProps) {
    /// <summary>
    /// Selected dealer ID from the Redux store.
    /// </summary>
    const dealerId = useAppSelector((state: RootState) => state.dealer.dealerId);

    /// <summary>
    /// Local state for list of dealer locations.
    /// </summary>
    const [dealerLocations, setDealerLocations] = useState<any>([]);

    /// <summary>
    /// Local state for currently selected dealer location ID.
    /// </summary>
    const [dealerLocationsID, setDealerLocationID] = useState<number>(-1);

    /// <summary>
    /// Loads dealer locations when the component mounts or dealerId changes.
    /// </summary>
    useEffect(() => {
        DashboardDataLoader.GetDealerLocations(dealerId).then((response) => {
            setDealerLocations(response);
        });
    }, []);

    /// <summary>
    /// Handles dealer location dropdown change and calls parent handler.
    /// </summary>
    /// <param name="value">Selected dealer location ID.</param>
    const handleDealerLocationChange = (value: number) => {
        setDealerLocationID(value);
        onDealerLocationChange(value);
    };

    return (
        <>
            <Helmet>
                <title>
                    {title} | {browserTitle}
                </title>
            </Helmet>

            <Box>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={2}
                >
                    <Typography variant="h6" gutterBottom fontWeight="bold">
                        {title}
                    </Typography>

                    <Stack direction="row" alignItems="center">
                        {showSearch && (
                            <TextField
                                placeholder="Search"
                                sx={{ width: "300px", mr: 2 }}
                                size="small"
                                //value={searchValue}
                                onChange={onSearchChange}
                                onKeyDown={onSearchEnter}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        )}

                        {showDealerFilter && (
                            <DealerLocationFilter
                                dealerLocationsID={dealerLocationsID}
                                dealerLocations={dealerLocations}
                                onDealerLocationChange={handleDealerLocationChange}
                            />
                        )}

                        {showDateRange && (
                            <DateRangePickerCustom
                                dateRange={dateRange}
                                onDateRangePickerChange={onDateRangePickerChange}
                                isDepositReport={false}
                            />
                        )}
                        {excelButtonProps.showExcelButton && (
                            <Button
                                title={excelButtonProps.title}
                                variant="contained"
                                startIcon={<DownloadIcon />}
                                sx={{ ml: 2 }}
                                onClick={excelButtonProps.onExportButtonClick}
                                disabled={excelButtonProps.isDisabled || excelButtonProps.isSubmitted}
                            >
                                {excelButtonProps.label}
                                {excelButtonProps.isSubmitted && (
                                    <span>
                                        <CircularProgress
                                            size={18}
                                            style={{ color: "blue", marginTop: 5, marginLeft: 6 }}
                                        />
                                    </span>
                                )}
                            </Button>
                            )
                        }
                    </Stack>
                </Stack>
            </Box>
        </>
    );
}
